import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError, setUnauthorizedHandler } from "@/lib/api";

const SESSION_REPLACED_HINT = "signed in on another device";
const SESSION_POLL_MS = 15000;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef(null);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser({
        id: data._id,
        email: data.email,
        name: data.name,
        role: data.role,
        clinic_id: data.clinic_id,
        must_change_password: data.must_change_password,
        clinic_subscription_status: data.clinic_subscription_status,
      });
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  // Single Active Session: this device's session may be revoked at any moment by a
  // login elsewhere. Every api call already flows through this handler on a 401 (see
  // lib/api.js interceptor); this instantly clears local auth state (redirecting via
  // ProtectedRoute) and shows the required message when that's the reason.
  useEffect(() => {
    setUnauthorizedHandler((detail) => {
      if (!userRef.current) return;
      if (typeof detail === "string" && detail.toLowerCase().includes(SESSION_REPLACED_HINT)) {
        toast.error(detail, { id: "session-replaced", duration: 10000 });
      }
      setUser(null);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Also poll periodically so an otherwise-idle tab (making no other API calls) still
  // detects a same-account login elsewhere and logs out promptly, not just on the
  // user's next click/navigation.
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      api.get("/auth/me").catch(() => {});
    }, SESSION_POLL_MS);
    return () => clearInterval(interval);
  }, [user]);

  const login = async (email, password, role) => {
    try {
      const { data } = await api.post("/auth/login", { email, password, role });
      setUser(data);
      return { success: true, user: data };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed (clearing local session anyway):", e);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
